import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Grid,
  Alert,
  FormHelperText
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { 
  checkRemolquePlacasExist, 
  getTrailersByTransportLine, 
  createTrailer, 
  updateTrailer, 
  deleteTrailer 
} from '../../api';

const RemolquesManagement = ({ selectedLine }) => {
  const [remolques, setRemolques] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openForm, setOpenForm] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentRemolque, setCurrentRemolque] = useState(null);
  const [formData, setFormData] = useState({
    placas: '',
    año: new Date().getFullYear(),
    tipo: '',
    modelo: '',
    color: '',
    economico: '',
    linea_transporte_id: selectedLine?.id || 0
  });
  const [errors, setErrors] = useState({
    placas: false,
    tipo: false
  });
  const [placasError, setPlacasError] = useState("");
  const [isValidatingPlacas, setIsValidatingPlacas] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Debounce function to prevent too many API calls
  const debounce = (func, delay) => {
    let timeoutId;
    return (...args) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  // Function to validate if placas already exist
  const validatePlacas = async (placas) => {
    if (!placas || placas.trim().length < 3) return;
    
    setIsValidatingPlacas(true);
    try {
      const response = await checkRemolquePlacasExist(placas.trim());
      if (response.exists) {
        // If we're editing an existing remolque and it's the same one, don't show an error
        if (editMode && currentRemolque && response.remolque_id === currentRemolque.id) {
          setPlacasError("");
        } else {
          setPlacasError(
            `Las placas ${placas} ya están registradas para la línea de transporte: ${response.linea_transporte.name} (${response.linea_transporte.razon_Social})`
          );
        }
      } else {
        setPlacasError("");
      }
    } catch (error) {
      console.error("Error validating remolque plates:", error);
    } finally {
      setIsValidatingPlacas(false);
    }
  };

  // Create a debounced version of validatePlacas
  const debouncedValidatePlacas = debounce(validatePlacas, 500);

  const fetchRemolques = async () => {
    if (!selectedLine) return;
    
    setLoading(true);
    try {
      const data = await getTrailersByTransportLine(selectedLine.id);
      setRemolques(data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar los remolques');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedLine) {
      fetchRemolques();
      setFormData(prev => ({
        ...prev,
        linea_transporte_id: selectedLine.id
      }));
    }
  }, [selectedLine]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for the field being updated
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }

    // Validate placas when the field changes
    if (name === 'placas') {
      debouncedValidatePlacas(value);
    }
  };

  const handleOpenForm = (remolque = null) => {
    setPlacasError(""); // Clear previous error
    if (remolque) {
      setFormData({
        placas: remolque.placas,
        año: remolque.año,
        tipo: remolque.tipo,
        modelo: remolque.modelo,
        color: remolque.color,
        economico: remolque.economico,
        linea_transporte_id: selectedLine.id
      });
      setCurrentRemolque(remolque);
      setEditMode(true);
    } else {
      setFormData({
        placas: '',
        año: new Date().getFullYear(),
        tipo: '',
        modelo: '',
        color: '',
        economico: '',
        linea_transporte_id: selectedLine.id
      });
      setCurrentRemolque(null);
      setEditMode(false);
    }
    setErrors({
      placas: false,
      tipo: false
    });
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setFormData({
      placas: '',
      año: new Date().getFullYear(),
      tipo: '',
      modelo: '',
      color: '',
      economico: '',
      linea_transporte_id: selectedLine?.id || 0
    });
    setErrors({
      placas: false,
      tipo: false
    });
    setPlacasError("");
  };

  const validateForm = () => {
    const newErrors = {
      placas: !formData.placas.trim(),
      tipo: !formData.tipo.trim()
    };
    
    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error) && !placasError;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Por favor complete los campos requeridos y corrija los errores');
      return;
    }

    if (placasError) {
      toast.error("Las placas ya están registradas para otra línea de transporte");
      return;
    }
    
    setSubmitting(true);
    
    try {
      if (editMode) {
        await updateTrailer(currentRemolque.id, formData);
        toast.success('Remolque actualizado con éxito');
      } else {
        await createTrailer(formData);
        toast.success('Remolque creado con éxito');
      }
      
      handleCloseForm();
      fetchRemolques();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Ocurrió un error. Inténtelo de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDelete = (remolque) => {
    setCurrentRemolque(remolque);
    setConfirmDelete(true);
  };

  const handleDelete = async () => {
    try {
      await deleteTrailer(currentRemolque.id);
      toast.success('Remolque eliminado con éxito');
      setConfirmDelete(false);
      fetchRemolques();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al eliminar el remolque');
    }
  };

  if (!selectedLine) {
    return (
      <Alert severity="info">
        Por favor selecciona una línea de transporte para administrar sus remolques
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Remolques de {selectedLine.name}</Typography>
          <Button 
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenForm()}
            disabled={selectedLine?.blocked}
          >
            Nuevo Remolque
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : remolques.length === 0 ? (
          <Alert severity="info">
            No hay remolques registrados para esta línea de transporte
          </Alert>
        ) : (
          <TableContainer>
            <Table sx={{ minWidth: 650 }} size="small" aria-label="tabla de remolques">
              <TableHead>
                <TableRow>
                  <TableCell>Placas</TableCell>
                  <TableCell>Económico</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Modelo</TableCell>
                  <TableCell>Año</TableCell>
                  <TableCell>Color</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {remolques.map((remolque) => (
                  <TableRow key={remolque.id}>
                    <TableCell>{remolque.placas}</TableCell>
                    <TableCell>{remolque.economico}</TableCell>
                    <TableCell>{remolque.tipo}</TableCell>
                    <TableCell>{remolque.modelo}</TableCell>
                    <TableCell>{remolque.año}</TableCell>
                    <TableCell>{remolque.color}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenForm(remolque)}
                        disabled={selectedLine?.blocked}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleConfirmDelete(remolque)}
                        disabled={selectedLine?.blocked}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Form Dialog */}
      <Dialog open={openForm} onClose={handleCloseForm} maxWidth="md" fullWidth>
        <DialogTitle>{editMode ? 'Editar Remolque' : 'Nuevo Remolque'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                name="placas"
                label="Placas"
                value={formData.placas}
                onChange={handleInputChange}
                fullWidth
                required
                error={errors.placas || !!placasError}
                helperText={errors.placas ? 'Las placas son obligatorias' : placasError}
                InputProps={{
                  endAdornment: isValidatingPlacas ? <CircularProgress size={20} /> : null
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                name="economico"
                label="Número Económico"
                value={formData.economico}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <FormControl fullWidth required error={errors.tipo}>
                <InputLabel>Tipo</InputLabel>
                <Select
                  name="tipo"
                  value={formData.tipo}
                  label="Tipo"
                  onChange={handleInputChange}
                >
                  <MenuItem value="CAJA 53FT SECA">Caja 53FT Seca</MenuItem>
                  <MenuItem value="CAJA 53FT REFRIGERADA">Caja 53FT Refrigerada</MenuItem>
                  <MenuItem value="CAJA 48FT SECA">Caja 48FT Seca</MenuItem>
                  <MenuItem value="CAJA 40FT SECA">Caja 40FT Seca</MenuItem>
                  <MenuItem value="CAJA 40FT REFRIGERADA">Caja 40FT Refrigerada</MenuItem>
                  <MenuItem value="PLATAFORMA 40FT">Plataforma 40FT</MenuItem>
                  <MenuItem value="LOWBOY">LowBoy</MenuItem>
                  <MenuItem value="CHASIS 40FT">Chasis 40FT</MenuItem>
                  <MenuItem value="CHASIS 20FT">Chasis 20FT</MenuItem>
                </Select>
                {errors.tipo && <FormHelperText>El tipo es obligatorio</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                name="modelo"
                label="Modelo"
                value={formData.modelo}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                name="año"
                label="Año"
                type="number"
                value={formData.año}
                onChange={handleInputChange}
                fullWidth
                required
                inputProps={{ min: 1990, max: new Date().getFullYear() + 1 }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                name="color"
                label="Color"
                value={formData.color}
                onChange={handleInputChange}
                fullWidth
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForm}>Cancelar</Button>
          <Button 
            onClick={handleSubmit}
            variant="contained" 
            color="primary"
            disabled={submitting || isValidatingPlacas || !!placasError}
          >
            {submitting ? 'Procesando...' : (editMode ? 'Actualizar' : 'Crear')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Está seguro de que desea eliminar el remolque con placas {currentRemolque?.placas}?
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RemolquesManagement;
